import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PumpAnalysis } from "@shared/schema";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { formatCurrency, formatNumber, formatPercent } from "@/lib/utils";
import { ResultsCharts } from "./results-charts";

interface ResultsRecommendationProps {
  analysis: PumpAnalysis;
  recommendations: any[];
}

export function ResultsRecommendation({ analysis, recommendations }: ResultsRecommendationProps) {
  const { t } = useTranslation();
  const [activeRecommendation, setActiveRecommendation] = useState(recommendations[0]?.manufacturer || "");
  const [activeSubtab, setActiveSubtab] = useState("specs");
  
  if (!recommendations || recommendations.length === 0) {
    return (
      <div className="py-8 text-center">
        <h3 className="text-lg font-medium mb-2">{t("results.noRecommendations")}</h3>
        <p className="text-muted-foreground">{t("results.noRecommendationsDescription")}</p>
      </div>
    );
  }
  
  // Set default active recommendation if not already set
  if (!activeRecommendation && recommendations[0]) {
    setActiveRecommendation(recommendations[0].manufacturer);
  }
  
  // Find the currently active recommendation data
  const currentRecommendation = recommendations.find(
    (rec) => rec.manufacturer === activeRecommendation
  ) || recommendations[0];
  
  return (
    <div>
      <h3 className="text-lg font-medium mb-4">{t("results.recommendedReplacements")}</h3>
      <p className="text-muted-foreground mb-6">{t("results.recommendedReplacementsDescription")}</p>
      
      {/* Recommendation Tabs */}
      <div className="border-b border-border mb-6">
        <div className="flex overflow-x-auto space-x-4">
          {recommendations.map((rec, index) => (
            <button
              key={index}
              onClick={() => setActiveRecommendation(rec.manufacturer)}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeRecommendation === rec.manufacturer
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground"
              }`}
            >
              {rec.manufacturer} {rec.model}
            </button>
          ))}
        </div>
      </div>
      
      {/* Subtabs */}
      <Tabs
        value={activeSubtab}
        onValueChange={setActiveSubtab}
        className="w-full"
      >
        <TabsList className="mb-6 grid-cols-6 w-full">
          <TabsTrigger value="specs">{t("results.subtabs.specs")}</TabsTrigger>
          <TabsTrigger value="curves">{t("results.subtabs.powerCurves")}</TabsTrigger>
          <TabsTrigger value="efficiency">{t("results.subtabs.efficiency")}</TabsTrigger>
          <TabsTrigger value="savings">{t("results.subtabs.savings")}</TabsTrigger>
          <TabsTrigger value="roi">{t("results.subtabs.roi")}</TabsTrigger>
          <TabsTrigger value="lifecycle">{t("results.subtabs.lifecycle")}</TabsTrigger>
        </TabsList>
        
        {/* Specs Tab Content */}
        <TabsContent value="specs">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-lg font-medium mb-4">{t("results.specs.title")}</h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <tbody>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.model")}</td>
                      <td className="px-4 py-3 text-sm">{currentRecommendation.model}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.head")}</td>
                      <td className="px-4 py-3 text-sm">{currentRecommendation.head} ft</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.flow")}</td>
                      <td className="px-4 py-3 text-sm">{currentRecommendation.flow} GPM</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("results.specs.motorPower")}</td>
                      <td className="px-4 py-3 text-sm">{currentRecommendation.hp} HP</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("results.specs.motorEfficiency")}</td>
                      <td className="px-4 py-3 text-sm">{formatPercent(currentRecommendation.motorEfficiency)}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("results.specs.pumpEfficiency")}</td>
                      <td className="px-4 py-3 text-sm">{formatPercent(currentRecommendation.pumpEfficiency)}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("results.specs.materials")}</td>
                      <td className="px-4 py-3 text-sm">{currentRecommendation.materials}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("results.specs.controlType")}</td>
                      <td className="px-4 py-3 text-sm">{currentRecommendation.controlType}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("results.specs.estimatedPrice")}</td>
                      <td className="px-4 py-3 text-sm">{formatCurrency(currentRecommendation.estimatedPrice)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-medium mb-4">{t("results.specs.compatibilityAssessment")}</h4>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-green-800">{t("results.specs.spaceFit")}</span>
                    <span className="text-sm text-green-800">{currentRecommendation.spaceFit}%</span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${currentRecommendation.spaceFit}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-green-800">{t("results.specs.retrofitEase")}</span>
                    <span className="text-sm text-green-800">{currentRecommendation.compatibilityScore}%</span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${currentRecommendation.compatibilityScore}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className={`p-3 rounded-lg ${
                  currentRecommendation.controlComplexity > 80 ? "bg-green-50" :
                  currentRecommendation.controlComplexity > 60 ? "bg-yellow-50" : "bg-red-50"
                }`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${
                      currentRecommendation.controlComplexity > 80 ? "text-green-800" :
                      currentRecommendation.controlComplexity > 60 ? "text-yellow-800" : "text-red-800"
                    }`}>{t("results.specs.controlComplexity")}</span>
                    <span className={`text-sm ${
                      currentRecommendation.controlComplexity > 80 ? "text-green-800" :
                      currentRecommendation.controlComplexity > 60 ? "text-yellow-800" : "text-red-800"
                    }`}>{currentRecommendation.controlComplexity}%</span>
                  </div>
                  <div className={`w-full rounded-full h-2 mt-2 ${
                    currentRecommendation.controlComplexity > 80 ? "bg-green-200" :
                    currentRecommendation.controlComplexity > 60 ? "bg-yellow-200" : "bg-red-200"
                  }`}>
                    <div 
                      className={`h-2 rounded-full ${
                        currentRecommendation.controlComplexity > 80 ? "bg-green-600" :
                        currentRecommendation.controlComplexity > 60 ? "bg-yellow-600" : "bg-red-600"
                      }`}
                      style={{ width: `${currentRecommendation.controlComplexity}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-green-800">{t("results.specs.overallRating")}</span>
                    <span className="text-sm text-green-800">{formatNumber((currentRecommendation.spaceFit + currentRecommendation.compatibilityScore + currentRecommendation.controlComplexity) / 3, 0)}%</span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${(currentRecommendation.spaceFit + currentRecommendation.compatibilityScore + currentRecommendation.controlComplexity) / 3}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Power Curves Tab Content */}
        <TabsContent value="curves">
          <div className="grid grid-cols-1 gap-6">
            {/* Main Power Curve Chart */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-2">{t("results.powerCurves.title") || "Power Consumption Curves"}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t("results.powerCurves.description") || "Comparison of power consumption between existing and recommended pumps at different flow rates. Lower curves indicate better energy efficiency."}
              </p>
              <div className="h-96">
                <ResultsCharts
                  type="powerCurve"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                />
              </div>
            </div>
            
            {/* Head-Flow Curve */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-2">{t("results.powerCurves.headFlowTitle") || "Head-Flow Characteristics"}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t("results.powerCurves.headFlowDescription") || "This curve shows the head (pressure) produced by the pump across its operating range. The system curve intersection points indicate where the pump will operate."}
              </p>
              <div className="h-80">
                <ResultsCharts
                  type="headFlowCurve"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Flow-Efficiency Curve */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="text-lg font-medium mb-2">{t("results.powerCurves.efficiencyCurveTitle") || "Flow-Efficiency Curve"}</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  {t("results.powerCurves.efficiencyCurveDescription") || "Shows how pump efficiency changes with flow rate. The peak represents the Best Efficiency Point (BEP)."}
                </p>
                <div className="h-72">
                  <ResultsCharts
                    type="efficiencyCurve"
                    recommendation={currentRecommendation}
                    analysis={analysis}
                  />
                </div>
              </div>
              
              {/* NPSH Curve */}
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="text-lg font-medium mb-2">{t("results.powerCurves.npshTitle") || "NPSH Requirements"}</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  {t("results.powerCurves.npshDescription") || "Net Positive Suction Head (NPSH) requirements across the flow range. Ensures adequate suction conditions to prevent cavitation."}
                </p>
                <div className="h-72">
                  <ResultsCharts
                    type="npshCurve"
                    recommendation={currentRecommendation}
                    analysis={analysis}
                  />
                </div>
              </div>
            </div>
            
            {/* Detailed Power Comparison Table */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-base font-medium mb-3">{t("results.powerCurves.comparisonTable") || "Power Consumption Comparison"}</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-muted">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("results.powerCurves.loadPoint") || "Load Point"}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("results.powerCurves.flow") || "Flow (GPM)"}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("results.powerCurves.currentPump") || "Current Pump (kW)"}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{currentRecommendation.model || "Recommended Pump (kW)"}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("results.powerCurves.reduction") || "Reduction"}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentRecommendation.powerCurve && currentRecommendation.powerCurve.map((point: any, index: number) => {
                      // Assuming original power is in the analysis somewhere, if not, we'd need to adjust this
                      const originalPower = point.originalPower || point.power * 1.4; // Calculation if original not available
                      const reduction = ((originalPower - point.power) / originalPower) * 100;
                      
                      return (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{point.loadPercentage}%</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatNumber(point.flow || (index + 1) * 100, 0)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatNumber(originalPower, 2)} kW</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatNumber(point.power, 2)} kW</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{formatNumber(reduction, 0)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-blue-700">
                    {t("results.powerCurves.operatingNote") || "Note: For optimal efficiency, the pump should operate as close as possible to its Best Efficiency Point (BEP). Operating far from BEP can lead to increased wear and energy consumption."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Efficiency Tab Content */}
        <TabsContent value="efficiency">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-4">{t("results.efficiency.title") || "Efficiency Comparison"}</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-muted">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("results.efficiency.component") || "Component"}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("results.efficiency.existing") || "Existing"}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{currentRecommendation.model || "Recommended"}</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("results.efficiency.improvement") || "Improvement"}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.efficiency.motorEfficiency") || "Motor Efficiency"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatPercent(analysis.efficiency || 75)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatPercent(currentRecommendation.motorEfficiency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        +{formatNumber(currentRecommendation.motorEfficiency - (analysis.efficiency || 75), 1)}%
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.efficiency.pumpEfficiency") || "Pump Efficiency"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatPercent((analysis.efficiency || 75) * 0.85)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatPercent(currentRecommendation.pumpEfficiency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        +{formatNumber(currentRecommendation.pumpEfficiency - ((analysis.efficiency || 75) * 0.85), 1)}%
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.efficiency.vfdEfficiency") || "VFD Efficiency"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {analysis.controlType?.includes('vfd') ? '95.0%' : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">97.0%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        {analysis.controlType?.includes('vfd') ? '+2.0%' : t("results.efficiency.new") || "New"}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.efficiency.systemEfficiency") || "System Efficiency"}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatPercent((analysis.efficiency || 75) * 0.8)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {formatPercent(currentRecommendation.systemEfficiency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        +{formatNumber(currentRecommendation.systemEfficiency - ((analysis.efficiency || 75) * 0.8), 1)}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6">
                <h4 className="text-base font-medium mb-3">{t("results.efficiency.efficiencyComparison") || "Efficiency Comparison"}</h4>
                <div className="h-64">
                  <ResultsCharts
                    type="efficiencyComparison"
                    recommendation={currentRecommendation}
                    analysis={analysis}
                  />
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-4">{t("results.efficiency.benefits") || "Efficiency Benefits"}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t("results.efficiency.benefit1") || "Premium efficiency motors offer 2-8% higher efficiency than standard motors"}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t("results.efficiency.benefit2") || "Improved impeller design reduces hydraulic losses and friction"}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t("results.efficiency.benefit3") || "Modern VFDs have lower harmonics and higher power factors"}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t("results.efficiency.benefit4") || "Properly sized pumps operate closer to their Best Efficiency Point"}</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>{t("results.efficiency.benefit5") || "Advanced materials reduce friction and improve durability"}</span>
                </li>
              </ul>
              
              <div className="mt-6">
                <h4 className="text-base font-medium mb-3">{t("results.efficiency.operatingProfile") || "Operating Profile Efficiency"}</h4>
                <div className="h-64">
                  <ResultsCharts
                    type="operatingEfficiency"
                    recommendation={currentRecommendation}
                    analysis={analysis}
                  />
                </div>
              </div>
            </div>
            
            {/* Efficiency Map */}
            <div className="bg-white p-4 rounded-lg shadow-sm col-span-2">
              <h4 className="text-lg font-medium mb-4">{t("results.efficiency.efficiencyMap") || "Efficiency Map at Different Operating Points"}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t("results.efficiency.efficiencyMapDescription") || "This chart shows the efficiency of the recommended pump at different flow rates and heads. The efficiency is highest at the design point and decreases as operation moves away from this point."}
              </p>
              <div className="h-96">
                <ResultsCharts
                  type="efficiencyMap"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {t("results.efficiency.efficiencyMapNote") || "Note: Pump efficiency shown as percentage. Best efficiency point (BEP) is typically at or near the design point."}
              </p>
            </div>
            
            {/* Wire-to-Water Efficiency Comparison */}
            <div className="bg-white p-4 rounded-lg shadow-sm col-span-2">
              <h4 className="text-lg font-medium mb-4">{t("results.efficiency.wireToWater") || "Wire-to-Water Efficiency Analysis"}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t("results.efficiency.wireToWaterDescription") || "Wire-to-water efficiency measures the overall system efficiency from electrical input to hydraulic output. This comprehensive metric accounts for losses in the motor, VFD, and pump."}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="text-sm font-medium mb-2">{t("results.efficiency.existingSystem") || "Existing System"}</h5>
                  <div className="h-72">
                    <ResultsCharts
                      type="wireToWaterExisting"
                      recommendation={currentRecommendation}
                      analysis={analysis}
                    />
                  </div>
                </div>
                <div>
                  <h5 className="text-sm font-medium mb-2">{t("results.efficiency.recommendedSystem") || "Recommended System"}</h5>
                  <div className="h-72">
                    <ResultsCharts
                      type="wireToWaterRecommended"
                      recommendation={currentRecommendation}
                      analysis={analysis}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-blue-700">
                    {t("results.efficiency.wireToWaterNote") || "Improving wire-to-water efficiency is the most effective way to reduce energy consumption in pumping systems. Even small improvements can yield significant energy savings over the system's lifetime."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Savings Tab Content */}
        <TabsContent value="savings">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-4">{t("results.savings.title") || "Energy Savings Summary"}</h4>
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.savings.currentEnergy") || "Current Energy Usage"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatNumber(currentRecommendation.annualEnergySavings * 2)} kWh/year
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.savings.projectedEnergy") || "Projected Energy Usage"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatNumber(currentRecommendation.annualEnergySavings)} kWh/year
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.savings.annualEnergySavings") || "Annual Energy Savings"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatNumber(currentRecommendation.annualEnergySavings)} kWh/year
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.savings.percentageReduction") || "Energy Reduction"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatPercent(50.0)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.savings.annualCostSavings") || "Annual Cost Savings"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(currentRecommendation.annualCostSavings)}/year
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.savings.tenYearSavings") || "10-Year Total Savings"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(currentRecommendation.annualCostSavings * 10)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-4">{t("results.savings.environmentalImpact") || "Environmental Impact"}</h4>
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.savings.annualCO2Reduction") || "Annual CO₂ Reduction"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatNumber(currentRecommendation.carbonReduction || currentRecommendation.annualEnergySavings * 0.000709, 2)} {t("common.tons") || "tons"}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.savings.equivalentTrees") || "Equivalent Trees Planted"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatNumber((currentRecommendation.carbonReduction || currentRecommendation.annualEnergySavings * 0.000709) * 45, 0)} {t("common.trees") || "trees"}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.savings.carEquivalent") || "Car Emissions Equivalent"}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatNumber((currentRecommendation.carbonReduction || currentRecommendation.annualEnergySavings * 0.000709) * 0.22, 1)} {t("common.cars") || "cars/year"}
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <div className="mt-6">
                <h4 className="text-base font-medium mb-3">{t("results.savings.carbonVisual") || "CO₂ Reduction Visualization"}</h4>
                <div className="h-64">
                  <ResultsCharts
                    type="carbonReduction"
                    recommendation={currentRecommendation}
                    analysis={analysis}
                  />
                </div>
              </div>
            </div>
            
            {/* Energy Usage Comparison */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-4">{t("results.savings.annualEnergyComparison") || "Energy Usage Comparison"}</h4>
              <div className="h-72">
                <ResultsCharts
                  type="energySavings"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                />
              </div>
            </div>
            
            {/* Savings Breakdown by Category */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-4">{t("results.savings.savingsBreakdown") || "Savings Breakdown"}</h4>
              <div className="h-72">
                <ResultsCharts
                  type="savingsBreakdown"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                />
              </div>
            </div>
            
            {/* Savings Over Time Projection */}
            <div className="bg-white p-4 rounded-lg shadow-sm col-span-2">
              <h4 className="text-lg font-medium mb-4">{t("results.savings.savingsOverTime") || "Savings Projection Over Time"}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t("results.savings.projectionDescription") || "This chart projects the cumulative energy cost savings over the lifetime of the equipment, factoring in inflation and energy price trends."}
              </p>
              <div className="h-80">
                <ResultsCharts
                  type="savingsProjection"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {t("results.savings.projectionNote") || "Note: Projections account for utility rate increases (3% annual) and inflation. Actual savings may vary based on usage patterns and future utility rates."}
              </p>
            </div>
            
            {/* Utility Rate Impact */}
            <div className="bg-white p-4 rounded-lg shadow-sm col-span-2">
              <h4 className="text-lg font-medium mb-4">{t("results.savings.utilityRateImpact") || "Utility Rate Sensitivity"}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t("results.savings.utilityRateDescription") || "This chart shows how fluctuations in utility rates affect 10-year savings projections, helping assess the investment under different energy price scenarios."}
              </p>
              <div className="h-72">
                <ResultsCharts
                  type="utilitySensitivity"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                  analyticsData={(analysis.results as any)?.analysis}
                />
              </div>
              <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-blue-700">
                    {t("results.savings.utilityRateNote") || "Higher utility rates increase the value of energy-efficient equipment and shorten payback periods. Even in low-rate scenarios, this ECM provides substantial savings."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* ROI Tab Content */}
        <TabsContent value="roi">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-4">{t("results.roi.costBreakdown")}</h4>
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.equipmentCost")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatCurrency(currentRecommendation.estimatedPrice)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.installationCost")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatCurrency(currentRecommendation.installationCost || currentRecommendation.estimatedPrice * 0.25)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.controlsIntegration")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatCurrency(500)} {/* Assuming a fixed cost for controls integration */}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.totalInitialCost")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {formatCurrency(
                        currentRecommendation.estimatedPrice + 
                        (currentRecommendation.installationCost || currentRecommendation.estimatedPrice * 0.25) + 
                        500
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.annualEnergySavings")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(currentRecommendation.annualCostSavings)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.annualMaintenanceSavings")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(currentRecommendation.maintenanceSavings || 250)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.totalAnnualSavings")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(
                        currentRecommendation.annualCostSavings + 
                        (currentRecommendation.maintenanceSavings || 250)
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-4">{t("results.roi.returnOnInvestment")}</h4>
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.simplePayback")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatNumber(currentRecommendation.paybackPeriod, 2)} {t("common.years")}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.breakEvenPoint")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatNumber(currentRecommendation.paybackPeriod * 1.1, 1)} {t("common.years")}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.npv")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(currentRecommendation.npv)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.irr")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatPercent(currentRecommendation.irr)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.roi.tenYearRoi")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatPercent(
                        (currentRecommendation.annualCostSavings * 10) / 
                        (currentRecommendation.estimatedPrice + 
                         (currentRecommendation.installationCost || currentRecommendation.estimatedPrice * 0.25) + 
                         500) * 100
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <svg className="h-5 w-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-yellow-700">
                    {t("results.roi.rebateNote") || "Utility rebates and incentives may be available to reduce initial costs. Contact your local utility provider for details."}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Waterfall Chart - Cost-Benefit Analysis */}
            <div className="bg-white p-4 rounded-lg shadow-sm col-span-2">
              <h4 className="text-lg font-medium mb-4">{t("results.roi.costBenefitAnalysis") || "10-Year Cost-Benefit Analysis"}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t("results.roi.waterfallDescription") || "This chart visualizes the cost-benefit breakdown over a 10-year period, showing how initial investments are offset by energy and maintenance savings."}
              </p>
              <div className="h-96">
                <ResultsCharts
                  type="waterfall"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                />
              </div>
            </div>
            
            {/* Sensitivity Analysis */}
            <div className="bg-white p-4 rounded-lg shadow-sm col-span-2">
              <h4 className="text-lg font-medium mb-4">{t("results.roi.sensitivityAnalysis") || "Sensitivity Analysis"}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t("results.roi.sensitivityDescription") || "This diagram shows how changes in key variables affect the project's net present value (NPV). Variables with larger bars have a greater impact on the financial outcome."}
              </p>
              <div className="h-80">
                <ResultsCharts
                  type="tornadoDiagram"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                  analyticsData={(analysis.results as any)?.analysis}
                />
              </div>
            </div>
          </div>
        </TabsContent>
        
        {/* Life-Cycle Tab Content */}
        <TabsContent value="lifecycle">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-4">{t("results.lifecycle.title")}</h4>
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.lifecycle.initialCost")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatCurrency(
                        currentRecommendation.estimatedPrice + 
                        (currentRecommendation.installationCost || currentRecommendation.estimatedPrice * 0.25) + 
                        500
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.lifecycle.tenYearEnergyCost")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatCurrency(
                        (currentRecommendation.annualEnergySavings * analysis.utilityRate * 10) / 2
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.lifecycle.tenYearMaintenanceCost")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatCurrency(
                        (analysis.maintenanceCost || 1000) * 10 * 0.5
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.lifecycle.tenYearTotalCost")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatCurrency(
                        (currentRecommendation.estimatedPrice + 
                        (currentRecommendation.installationCost || currentRecommendation.estimatedPrice * 0.25) + 
                        500) +
                        ((currentRecommendation.annualEnergySavings * analysis.utilityRate * 10) / 2) +
                        ((analysis.maintenanceCost || 1000) * 10 * 0.5)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.lifecycle.existingSystemCost")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatCurrency(
                        (currentRecommendation.annualEnergySavings * analysis.utilityRate * 10) +
                        ((analysis.maintenanceCost || 1000) * 10)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.lifecycle.tenYearSavings")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(
                        ((currentRecommendation.annualEnergySavings * analysis.utilityRate * 10) +
                        ((analysis.maintenanceCost || 1000) * 10)) -
                        ((currentRecommendation.estimatedPrice + 
                        (currentRecommendation.installationCost || currentRecommendation.estimatedPrice * 0.25) + 
                        500) +
                        ((currentRecommendation.annualEnergySavings * analysis.utilityRate * 10) / 2) +
                        ((analysis.maintenanceCost || 1000) * 10 * 0.5))
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h4 className="text-lg font-medium mb-4">{t("results.lifecycle.environmentalImpact")}</h4>
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.lifecycle.annualCO2Reduction")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatNumber(currentRecommendation.carbonReduction || currentRecommendation.annualEnergySavings * 0.000709, 2)} {t("common.tons")}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.lifecycle.tenYearCO2Reduction")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatNumber((currentRecommendation.carbonReduction || currentRecommendation.annualEnergySavings * 0.000709) * 10, 2)} {t("common.tons")}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-muted-foreground">{t("results.lifecycle.equivalentTrees")}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {formatNumber((currentRecommendation.carbonReduction || currentRecommendation.annualEnergySavings * 0.000709) * 45, 0)} {t("results.lifecycle.trees")}
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex">
                  <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-blue-700">
                    {t("results.lifecycle.analysisNote", {
                      inflation: analysis.inflationRate || 2.0,
                      discount: analysis.discountRate || 3.5
                    }) || `Lifecycle cost analysis accounts for inflation (${analysis.inflationRate || 2.0}%) and a discount rate of ${analysis.discountRate || 3.5}%.`}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Lifecycle Cost Chart */}
            <div className="bg-white p-4 rounded-lg shadow-sm col-span-2">
              <h4 className="text-lg font-medium mb-4">{t("results.lifecycle.costOverTime") || "Lifecycle Cost Comparison"}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t("results.lifecycle.costOverTimeDescription") || "This chart compares the cumulative costs of the current system versus the recommended replacement over time, including capital, energy, and maintenance costs."}
              </p>
              <div className="h-96">
                <ResultsCharts
                  type="lifecycleCost"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                />
              </div>
            </div>
            
            {/* Energy Use Profile */}
            <div className="bg-white p-4 rounded-lg shadow-sm col-span-2">
              <h4 className="text-lg font-medium mb-4">{t("results.lifecycle.loadProfile") || "Typical Load Profile"}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                {t("results.lifecycle.loadProfileDescription") || "This chart shows the typical daily load profile used in the energy calculations. The recommended solution is sized to efficiently handle this usage pattern."}
              </p>
              <div className="h-80">
                <ResultsCharts
                  type="loadProfile"
                  recommendation={currentRecommendation}
                  analysis={analysis}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {t("results.lifecycle.loadProfileNote") || "Note: Operating the pump closer to its Best Efficiency Point (BEP) improves both efficiency and equipment longevity."}
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
